import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { storage } from '../utils/helpers';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE || 'https://cafedemoapi-hdhcagczfue3gbed.canadacentral-01.azurewebsites.net/api',
  prepareHeaders: (headers) => {
    const token = storage.get('accessToken');
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Try to refresh token
    const refreshToken = storage.get('refreshToken');
    if (refreshToken) {
      const refreshResult = await baseQuery(
        {
          url: '/auth/refresh-token',
          method: 'POST',
          body: { refreshToken },
        },
        api,
        extraOptions
      );

      if (refreshResult.data) {
        storage.set('accessToken', refreshResult.data.data.accessToken);
        storage.set('refreshToken', refreshResult.data.data.refreshToken);
        
        // Retry original request
        result = await baseQuery(args, api, extraOptions);
      } else {
        // Refresh failed, logout
        storage.remove('accessToken');
        storage.remove('refreshToken');
        storage.remove('user');
        window.location.href = '/login';
      }
    } else {
      // No refresh token, logout
      storage.remove('accessToken');
      storage.remove('refreshToken');
      storage.remove('user');
      window.location.href = '/login';
    }
  }

  return result;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Auth',
    'Restaurant',
    'Menu',
    'Orders',
    'Customers',
    'KPIs',
    'Storefront'
  ],
  endpoints: (builder) => ({
    // Auth endpoints
    login: builder.mutation({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Auth'],
    }),

    registerOwner: builder.mutation({
      query: (data) => {
        const formData = new FormData();
        Object.keys(data).forEach(key => {
          if (key === 'logo' && data[key]) {
            formData.append('logo', data[key]);
          } else {
            formData.append(key, data[key]);
          }
        });
        return {
          url: '/auth/register-owner',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Auth'],
    }),

    logout: builder.mutation({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      invalidatesTags: ['Auth'],
    }),

    getMe: builder.query({
      query: () => '/auth/me',
      providesTags: ['Auth'],
    }),

    // Slug endpoints
    checkSlug: builder.query({
      query: (name) => `/slug/check?name=${encodeURIComponent(name)}`,
    }),

    // Admin endpoints
    getAdminRestaurants: builder.query({
      query: ({ page = 1, limit = 20, search = '' }) => 
        `/admin/restaurants?page=${page}&limit=${limit}&search=${search}`,
      providesTags: ['Restaurant'],
    }),

    createRestaurant: builder.mutation({
      query: (data) => {
        const formData = new FormData();
        Object.keys(data).forEach(key => {
          if (key === 'logo' && data[key]) {
            formData.append('logo', data[key]);
          } else {
            formData.append(key, data[key]);
          }
        });
        return {
          url: '/admin/restaurants',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Restaurant'],
    }),

    updateRestaurant: builder.mutation({
      query: ({ id, ...data }) => {
        const formData = new FormData();
        Object.keys(data).forEach(key => {
          if (key === 'logo' && data[key]) {
            formData.append('logo', data[key]);
          } else {
            formData.append(key, data[key]);
          }
        });
        return {
          url: `/admin/restaurants/${id}`,
          method: 'PATCH',
          body: formData,
        };
      },
      invalidatesTags: ['Restaurant'],
    }),

    getAdminKPIs: builder.query({
      query: () => '/admin/kpis',
      providesTags: ['KPIs'],
    }),

    getAdminCustomers: builder.query({
      query: ({ page = 1, limit = 20, search = '' }) => 
        `/admin/customers?page=${page}&limit=${limit}&search=${search}`,
      providesTags: ['Customers'],
    }),

    // Owner endpoints
    getOwnerKPIs: builder.query({
      query: () => '/owner/kpis',
      providesTags: ['KPIs'],
    }),

    getRecentOrders: builder.query({
      query: (limit = 10) => `/owner/recent-orders?limit=${limit}`,
      providesTags: ['Orders'],
    }),

    getPopularItems: builder.query({
      query: (days = 30) => `/owner/popular-items?days=${days}`,
      providesTags: ['Menu'],
    }),

    getOrders: builder.query({
      query: ({ page = 1, limit = 20, search = '', status = '' }) => {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', limit);
        if (search) params.append('search', search);
        if (status) params.append('status', status);
        return `/owner/orders?${params.toString()}`;
      },
      providesTags: ['Orders'],
    }),

    updateOrderStatus: builder.mutation({
      query: ({ orderId, status }) => ({
        url: `/owner/orders/${orderId}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: ['Orders'],
    }),

    deleteOrder: builder.mutation({
      query: (orderId) => ({
        url: `/owner/orders/${orderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Orders'],
    }),

    exportOrdersCSV: builder.mutation({
      query: () => ({
        url: '/owner/orders/export.csv',
        responseHandler: (response) => response.blob(),
      }),
    }),

    exportCustomersCSV: builder.mutation({
      query: () => ({
        url: '/owner/customers/export.csv',
        responseHandler: (response) => response.blob(),
      }),
    }),

    getReceipt: builder.query({
      query: (orderId) => ({
        url: `/owner/orders/${orderId}/receipt`,
        responseHandler: (response) => response.text(),
      }),
    }),

    getCustomers: builder.query({
      query: ({ page = 1, limit = 20, search = '' }) => 
        `/owner/customers?page=${page}&limit=${limit}&search=${search}`,
      providesTags: ['Customers'],
    }),

    getMenu: builder.query({
      query: ({ page = 1, limit = 20, search = '', category = '', available = '' }) => {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', limit);
        if (search) params.append('search', search);
        if (category) params.append('category', category);
        if (available) params.append('available', available);
        return `/owner/menu?${params.toString()}`;
      },
      providesTags: ['Menu'],
    }),

    createMenuItem: builder.mutation({
      query: (data) => {
        const formData = new FormData();
        Object.keys(data).forEach(key => {
          if (key === 'image' && data[key]) {
            formData.append('image', data[key]);
          } else {
            formData.append(key, data[key]);
          }
        });
        return {
          url: '/owner/menu',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Menu'],
    }),

    updateMenuItem: builder.mutation({
      query: ({ id, ...data }) => {
        const formData = new FormData();
        Object.keys(data).forEach(key => {
          if (key === 'image' && data[key]) {
            formData.append('image', data[key]);
          } else {
            formData.append(key, data[key]);
          }
        });
        return {
          url: `/owner/menu/${id}`,
          method: 'PATCH',
          body: formData,
        };
      },
      invalidatesTags: ['Menu'],
    }),

    deleteMenuItem: builder.mutation({
      query: (id) => ({
        url: `/owner/menu/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Menu'],
    }),

    getStore: builder.query({
      query: () => '/owner/store',
      providesTags: ['Restaurant'],
    }),

    updateStore: builder.mutation({
      query: (data) => {
        const formData = new FormData();
        Object.keys(data).forEach(key => {
          if (key === 'logo' && data[key]) {
            formData.append('logo', data[key]);
          } else {
            formData.append(key, data[key]);
          }
        });
        return {
          url: '/owner/store',
          method: 'PATCH',
          body: formData,
        };
      },
      invalidatesTags: ['Restaurant'],
    }),

    // Storefront endpoints
    getStoreInfo: builder.query({
      query: (shopSlug) => `/store/${shopSlug}/info`,
      providesTags: ['Storefront'],
    }),

    getStoreMenu: builder.query({
      query: ({ shopSlug, search = '', category = '', available = 'true' }) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (category) params.append('category', category);
        params.append('available', available);
        return `/store/${shopSlug}/menu?${params.toString()}`;
      },
      providesTags: ['Storefront', 'Menu'],
    }),

    checkout: builder.mutation({
      query: ({ shopSlug, ...data }) => ({
        url: `/store/${shopSlug}/checkout`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Orders'],
    }),

    getCustomerOrders: builder.query({
      query: ({ shopSlug, customerEmail }) => 
        `/store/${shopSlug}/orders?customerEmail=${encodeURIComponent(customerEmail)}`,
      providesTags: ['Orders'],
    }),
  }),
});

export const {
  // Auth
  useLoginMutation,
  useRegisterOwnerMutation,
  useLogoutMutation,
  useGetMeQuery,
  
  // Slug
  useCheckSlugQuery,
  
  // Admin
  useGetAdminRestaurantsQuery,
  useCreateRestaurantMutation,
  useUpdateRestaurantMutation,
  useGetAdminKPIsQuery,
  useGetAdminCustomersQuery,
  
  // Owner
  useGetOwnerKPIsQuery,
  useGetRecentOrdersQuery,
  useGetPopularItemsQuery,
  useGetOrdersQuery,
  useUpdateOrderStatusMutation,
  useDeleteOrderMutation,
  useExportOrdersCSVMutation,
  useExportCustomersCSVMutation,
  useGetReceiptQuery,
  useGetCustomersQuery,
  useGetMenuQuery,
  useCreateMenuItemMutation,
  useUpdateMenuItemMutation,
  useDeleteMenuItemMutation,
  useGetStoreQuery,
  useUpdateStoreMutation,
  
  // Storefront
  useGetStoreInfoQuery,
  useGetStoreMenuQuery,
  useCheckoutMutation,
  useGetCustomerOrdersQuery,
} = api;